import type { Ref } from 'react'

import { Checkbox, DatePicker, Input, Label, Select, Text, Toggle } from '@/ui'
import { TypographyStyles } from '@/styles'
import { DATE_FORMATS } from '@/utils/dateHelpers'

import {
  TaxfyleQuestionType,
  type TaxfyleAnswer,
  type TaxfyleShortFormQuestion,
} from './taxfyleShortForm.slice'

const BOOLEAN_OPTIONS = [
  { key: 'yes', value: true, text: 'Yes' },
  { key: 'no', value: false, text: 'No' },
]

/**
 * Taxfyle owns the question set, so rendering is driven entirely by the
 * payload's type rather than a local schema.
 */
export const TaxfyleQuestionField = ({
  question,
  value,
  onChange,
  missing = false,
  fieldRef,
}: {
  question: TaxfyleShortFormQuestion
  value: TaxfyleAnswer
  onChange: (value: TaxfyleAnswer) => void
  missing?: boolean
  fieldRef?: Ref<HTMLDivElement>
}) => {
  const renderInput = () => {
    switch (question.type) {
      case TaxfyleQuestionType.bool:
        return (
          <div className="flex gap-3">
            {BOOLEAN_OPTIONS.map((option) => (
              <Toggle
                key={option.key}
                variant="outline"
                size="lg"
                className="flex-1"
                checked={value === option.value}
                onCheckedChange={() => onChange(option.value)}
              >
                {option.text}
              </Toggle>
            ))}
          </div>
        )

      case TaxfyleQuestionType.numeric:
        return (
          <Input
            type="number"
            min={0}
            value={value === null ? '' : String(value)}
            onChange={(e) =>
              onChange(e.target.value === '' ? null : Number(e.target.value))
            }
          />
        )

      case TaxfyleQuestionType.text:
        return (
          <Input
            value={value === null ? '' : String(value)}
            onChange={(e) => onChange(e.target.value)}
          />
        )

      case TaxfyleQuestionType.dateTime:
        return (
          <DatePicker
            dateFormat={DATE_FORMATS.INPUT}
            value={typeof value === 'string' ? value : undefined}
            onChange={(val) => onChange(val)}
          />
        )

      case TaxfyleQuestionType.singleSelect:
        return (
          <Select
            options={question.options?.map((option) => ({
              key: option.id,
              value: option.id,
              text: option.text,
            }))}
            value={typeof value === 'string' ? value : undefined}
            placeholder="Select one"
            onValueChange={(val) => onChange(val)}
          />
        )

      case TaxfyleQuestionType.multiSelect: {
        const selected = Array.isArray(value) ? value : []
        return (
          <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
            {question.options?.map((option) => (
              <label key={option.id} className="flex items-center gap-2">
                <Checkbox
                  checked={selected.includes(option.id)}
                  onCheckedChange={(checked) =>
                    onChange(
                      checked
                        ? [...selected, option.id]
                        : selected.filter((id) => id !== option.id)
                    )
                  }
                />
                <Text>{option.text}</Text>
              </label>
            ))}
          </div>
        )
      }

      default:
        return question.type satisfies never
    }
  }

  return (
    <div ref={fieldRef} className="flex flex-col gap-2">
      <Label className={missing ? 'text-danger' : undefined}>
        {question.question}
      </Label>
      {question.description && (
        <Text variant={TypographyStyles.BODY_SM} className="text-neutral-600">
          {question.description}
        </Text>
      )}
      {/* Ringed rather than bordered: it sits outside whatever control the
          question type renders, none of which share an error state. */}
      <div className={missing ? 'rounded-lg ring-2 ring-danger-50' : undefined}>
        <fieldset disabled={question.readOnly} className="contents">
          {renderInput()}
        </fieldset>
      </div>
      {missing && (
        <Text variant={TypographyStyles.BODY_SM} className="text-danger">
          Please answer this question.
        </Text>
      )}
    </div>
  )
}
